import { itemApi } from "@api/items";
import { withCancel } from "@api/utils";
import { Error } from "@components/Error";
import { Loader } from "@components/Loader";
import { RJSForm } from "@forms/rjsf";
import { goTo } from "@history";
import { BackOfficeRoutes } from "@routes/urls";
import _get from "lodash/get";
import _merge from "lodash/merge";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { Message } from "semantic-ui-react";
import { schema } from "./schema";
import { uiSchema } from "./uiSchema";

// Constants
const ERROR_MESSAGES = {
  shelf: {
    duplicate: "Shelf already exists. Please choose a different shelf.",
  },
  rack: {
    duplicate: "Rack already exists. Please choose a different rack.",
  },
  call_number_suffix: {
    duplicate:
      "Call number suffix already exists for this document. Please choose a different suffix.",
  },
};

const ERROR_TIMEOUT = 5000;
const SEARCH_SIZE = 10;

export class ItemEditor extends Component {
  constructor(props) {
    super(props);

    const isEditing = !!props.match.params.itemPid;
    const prefilledFormData = isEditing
      ? {}
      : _get(props, "location.state.formData", {});

    this.state = {
      data: { metadata: prefilledFormData },
      isLoading: isEditing,
      error: {},
      validationErrors: { shelf: null, rack: null, call_number_suffix: null },
      showFloatingErrors: {
        shelf: false,
        rack: false,
        call_number_suffix: false,
      },
      isSubmitting: false,
      formKey: 0,
      lastFormData: {},
    };

    this.timeouts = {};
    this.cancellables = {};
  }

  componentDidMount() {
    const { match } = this.props;
    if (match.params.itemPid) {
      this.fetchItem(match.params.itemPid);
    }
  }

  componentWillUnmount() {
    Object.values(this.cancellables).forEach((cancellable) => {
      cancellable && cancellable.cancel();
    });

    // Clear all timeouts
    Object.values(this.timeouts).forEach((timeout) => {
      clearTimeout(timeout);
    });
  }

  get userIsEditing() {
    return !!this.props.match.params.itemPid;
  }

  fetchItem = async (itemPid) => {
    this.cancellables.fetchItem = withCancel(itemApi.get(itemPid));
    try {
      const response = await this.cancellables.fetchItem.promise;
      this.setState({ data: response.data, isLoading: false, error: {} });
    } catch (error) {
      if (error !== "UNMOUNTED") {
        this.setState({ isLoading: false, error: error });
      }
    }
  };

  handleISBNDataFetched = (mappedData) => {
    // Check if this is an error response
    if (mappedData && mappedData.error) {
      return;
    }

    this.setState((prevState) => ({
      data: {
        ...prevState.data,
        metadata: {
          ...prevState.data.metadata,
          ...mappedData,
        },
      },
      formKey: prevState.formKey + 1,
    }));
  };

  checkUniqueness = async (
    field,
    value,
    currentItemPid = null,
    additionalFilters = ""
  ) => {
    try {
      const searchQuery = `${field}:"${value}"${additionalFilters}`;
      this.cancellables[`${field}Check`] = withCancel(
        itemApi.list(searchQuery, { size: SEARCH_SIZE })
      );

      const response = await this.cancellables[`${field}Check`].promise;
      const existingItems = response.data.hits;

      const duplicates = currentItemPid
        ? existingItems.filter((item) => item.metadata.pid !== currentItemPid)
        : existingItems;

      return duplicates.length === 0;
    } catch (error) {
      if (error !== "UNMOUNTED") {
        return true;
      }
      return true;
    }
  };

  // Removed required field and pattern validations
  validateField = (field, value) => {
    if (!value || value.trim() === "") {
      return null; //No error for empty values
    }

    return null;
  };

  showFloatingError = (field, errorMessage) => {
    this.setState((prevState) => ({
      validationErrors: {
        ...prevState.validationErrors,
        [field]: errorMessage,
      },
      showFloatingErrors: {
        ...prevState.showFloatingErrors,
        [field]: true,
      },
    }));

    this.timeouts[field] = setTimeout(() => {
      this.setState((prevState) => ({
        showFloatingErrors: {
          ...prevState.showFloatingErrors,
          [field]: false,
        },
      }));
    }, ERROR_TIMEOUT);
  };

  hideFloatingError = (field) => {
    this.setState((prevState) => ({
      showFloatingErrors: {
        ...prevState.showFloatingErrors,
        [field]: false,
      },
    }));

    if (this.timeouts[field]) {
      clearTimeout(this.timeouts[field]);
    }
  };

  preValidateForm = async (formData) => {
    const {
      match: {
        params: { itemPid },
      },
    } = this.props;
    const cleanFormData = { ...formData };
    delete cleanFormData.isbn_lookup;

    let hasErrors = false;

    // Check uniqueness for shelf and rack (globally)
    for (const field of ["shelf", "rack"]) {
      if (cleanFormData[field]) {
        const isUnique = await this.checkUniqueness(
          field,
          cleanFormData[field],
          itemPid
        );
        if (!isUnique) {
          this.showFloatingError(field, ERROR_MESSAGES[field].duplicate);
          hasErrors = true;
        }
      }
    }

    // Check uniqueness for call_number_suffix (scoped to same document)
    if (cleanFormData.call_number_suffix) {
      const documentPid = cleanFormData.document_pid;
      if (documentPid) {
        const additionalFilters = ` AND document_pid:"${documentPid}"`;
        const isUnique = await this.checkUniqueness(
          "call_number_suffix",
          cleanFormData.call_number_suffix,
          itemPid,
          additionalFilters
        );
        if (!isUnique) {
          this.showFloatingError(
            "call_number_suffix",
            ERROR_MESSAGES.call_number_suffix.duplicate
          );
          hasErrors = true;
        }
      }
    }

    return !hasErrors;
  };

  submitAction = async (formData) => {
    const {
      match: {
        params: { itemPid },
      },
    } = this.props;

    this.setState({
      isSubmitting: true,
      lastFormData: formData,
    });

    const isValid = await this.preValidateForm(formData);
    if (!isValid) {
      this.setState({ isSubmitting: false });
      return Promise.reject({ preventDefault: true });
    }

    this.clearValidationErrors();

    try {
      const cleanFormData = { ...formData };
      delete cleanFormData.isbn_lookup;

      const isEditing = !!itemPid;
      const result = isEditing
        ? await itemApi.update(itemPid, cleanFormData)
        : await itemApi.create(cleanFormData);

      this.setState({ isSubmitting: false });
      return result;
    } catch (error) {
      this.setState({ isSubmitting: false });
      throw error;
    }
  };

  successCallback = (response) => {
    goTo(BackOfficeRoutes.itemDetailsFor(response.data.metadata.pid));
  };

  handleFormChange = (formData) => {
    this.setState((prevState) => ({
      data: {
        ...prevState.data,
        metadata: formData,
      },
      lastFormData: formData, // Keep track of the latest form data
    }));

    // Clear validation errors when form changes
    const hasAnyError = Object.values(this.state.validationErrors).some(
      (error) => error
    );
    if (hasAnyError) {
      this.clearValidationErrors();
    }
  };

  clearValidationErrors = () => {
    this.setState({
      validationErrors: { shelf: null, rack: null, call_number_suffix: null },
      showFloatingErrors: {
        shelf: false,
        rack: false,
        call_number_suffix: false,
      },
    });
  };

  renderFloatingError = (field, position) => {
    const { validationErrors, showFloatingErrors } = this.state;
    const error = validationErrors[field];
    const shouldShow = showFloatingErrors[field];

    if (!error || !shouldShow) {
      return null;
    }

    return (
      <div
        style={{
          position: "fixed",
          top: position,
          right: "20px",
          zIndex: 1000,
          maxWidth: "400px",
          animation: "slideIn 0.3s ease-out",
        }}
      >
        <Message
          negative
          onDismiss={() => this.hideFloatingError(field)}
          header={`${field.charAt(0).toUpperCase() + field.slice(1)} Invalid`}
          content={error}
        />

        <style>
          {`
            @keyframes slideIn {
              from {
                transform: translateX(100%);
                opacity: 0;
              }
              to {
                transform: translateX(0);
                opacity: 1;
              }
            }
          `}
        </style>
      </div>
    );
  };

  getEnhancedSchema = () => {
    const originalSchema = schema();
    return {
      ...originalSchema,
      properties: {
        isbn_lookup: {
          type: "string",
          title: "ISBN Lookup",
          description: "Enter ISBN to auto-populate fields (optional)",
        },
        ...originalSchema.properties,
      },
    };
  };

  getEnhancedUiSchema = (title) => {
    const originalUiSchema = uiSchema(title);
    return {
      ...originalUiSchema,
      isbn_lookup: {
        "ui:field": "isbnLookupField",
        "ui:options": {
          onDataFetched: this.handleISBNDataFetched,
        },
      },
      "custom:grid": [
        { isbn_lookup: 16 },
        { "custom:divider": 16 },
        ...originalUiSchema["custom:grid"],
      ],
    };
  };

  render() {
    const {
      match: {
        params: { itemPid },
      },
    } = this.props;
    const { isLoading, error, data, isSubmitting, formKey, lastFormData } =
      this.state;

    const isEditing = !!itemPid;
    const formTitle = isEditing ? `Item - Edit #${itemPid}` : "Item - Create";
    const successMessage = isEditing
      ? "The item was successfully updated."
      : "The item was successfully created.";

    const formData =
      lastFormData && Object.keys(lastFormData).length > 0
        ? lastFormData
        : isEditing
        ? data.metadata || {}
        : _merge(
            {},
            _get(this.props, "location.state.formData", {}),
            data.metadata
          );

    return (
      <div style={{ position: "relative" }}>
        {this.renderFloatingError("shelf", "20px")}
        {this.renderFloatingError("rack", "80px")}
        {this.renderFloatingError("call_number_suffix", "140px")}

        {isEditing ? (
          <Loader isLoading={isLoading || isSubmitting}>
            <Error error={error}>
              <RJSForm
                key={`edit-${itemPid}-${formKey}`}
                schema={this.getEnhancedSchema()}
                uiSchema={this.getEnhancedUiSchema(formTitle)}
                formData={formData}
                submitAction={this.submitAction}
                successCallback={this.successCallback}
                successMessage={successMessage}
                onChange={this.handleFormChange}
              />
            </Error>
          </Loader>
        ) : (
          <Error error={error}>
            <RJSForm
              key={`create-${formKey}`}
              schema={this.getEnhancedSchema()}
              uiSchema={this.getEnhancedUiSchema(formTitle)}
              formData={formData}
              submitAction={this.submitAction}
              successCallback={this.successCallback}
              successMessage={successMessage}
              onChange={this.handleFormChange}
            />
          </Error>
        )}
      </div>
    );
  }
}

ItemEditor.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      itemPid: PropTypes.string,
    }),
  }).isRequired,
  location: PropTypes.shape({
    state: PropTypes.shape({
      formData: PropTypes.object,
      extraData: PropTypes.object,
    }),
  }),
};

ItemEditor.defaultProps = {
  location: {
    state: null,
  },
};
